<template>
    <span class="mk-sparkle">
        <span ref="el">
            <slot></slot>
        </span>
        <svg
            v-for="particle in particles"
            :key="particle.id"
            :width="width + 64"
            :height="height + 64"
            :viewBox="`0 0 ${width + 64} ${height + 64}`"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                style="transform-origin: center; transform-box: fill-box"
                :transform="`translate(${particle.x} ${particle.y})`"
                :fill="particle.color"
                d="M29.427,2.011C29.721,0.83 30.782,0 32,0C33.218,0 34.279,0.83 34.573,2.011L39.455,21.646C39.629,22.347 39.991,22.987 40.502,23.498C41.013,24.009 41.653,24.371 42.354,24.545L61.989,29.427C63.17,29.721 64,30.782 64,32C64,33.218 63.17,34.279 61.989,34.573L42.354,39.455C41.653,39.629 41.013,39.991 40.502,40.502C39.991,41.013 39.629,41.653 39.455,42.354L34.573,61.989C34.279,63.17 33.218,64 32,64C30.782,64 29.721,63.17 29.427,61.989L24.545,42.354C24.371,41.653 24.009,41.013 23.498,40.502C22.987,39.991 22.347,39.629 21.646,39.455L2.011,34.573C0.83,34.279 0,33.218 0,32C0,30.782 0.83,29.721 2.011,29.427L21.646,24.545C22.347,24.371 22.987,24.009 23.498,23.498C24.009,22.987 24.371,22.347 24.545,21.646L29.427,2.011Z"
            >
                <animateTransform
                    attributeName="transform"
                    attributeType="XML"
                    type="rotate"
                    from="0 0 0"
                    to="360 0 0"
                    :dur="`${particle.dur}ms`"
                    repeatCount="1"
                    additive="sum"
                />
                <animateTransform
                    attributeName="transform"
                    attributeType="XML"
                    type="scale"
                    @endEvent="removeParticle(particle.id)"
                    :values="`0; ${particle.size}; 0`"
                    :dur="`${particle.dur}ms`"
                    repeatCount="1"
                    fill="freeze"
                    additive="sum"
                />
            </path>
        </svg>
    </span>
</template>

<script lang="ts" setup>
import { ref, useTemplateRef, watchEffect } from "vue";
import {
    useElementSize,
    useElementVisibility,
    useIntervalFn,
    usePreferredReducedMotion,
} from "@vueuse/core";

type Particle = {
    id: string;
    x: number;
    y: number;
    size: number;
    dur: number;
    alive: boolean;
    color: string;
};

const particles = ref([] as Particle[]);
const el = useTemplateRef<HTMLElement>("el");
const colors = ["#eb6f92", "#9ccfd8", "#f6c177", "#f6c177", "#f6c177"];

const reducedMotion = usePreferredReducedMotion();
const visible = useElementVisibility(el);
const { width, height } = useElementSize(el, undefined, { box: "border-box" });

const removeParticle = (id: string) => {
    particles.value = particles.value.filter((p) => p.id !== id);
};

const frequency = ref(500 + Math.random() * 500);
const { pause, resume } = useIntervalFn(() => {
    const x = Math.random() * width.value;
    const y = Math.random() * height.value;

    const sizeFactor = Math.random();
    const now = Date.now();
    const particle = {
        id: `sparkle:${now}`,
        x,
        y,
        size: 0.2 + (sizeFactor / 10) * 3,
        dur: 1000 + sizeFactor * 1000,
        alive: true,
        color: colors[Math.floor(Math.random() * colors.length)],
    };

    particles.value.push(particle);
    frequency.value = 500 + Math.random() * 500;
}, frequency);

watchEffect(() => {
    if (reducedMotion.value === "reduce" || !visible.value) {
        pause();
        return;
    }

    resume();
});
</script>

<style lang="scss" scoped>
.mk-sparkle {
    position: relative;
    display: inline-block;

    > span {
        display: inline-block;
    }

    > svg {
        position: absolute;
        top: -32px;
        left: -32px;
        pointer-events: none;
    }
}
</style>
